import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Smartphone, Wrench, Compass } from "lucide-react";
import { KineticText } from "@/components/KineticText";
import { Reveal } from "@/components/Reveal";
import { MagneticButton } from "@/components/MagneticButton";

const chapters = [
  { n: "01", title: "Écouter", text: "Comprendre votre métier, vos utilisateurs et vos contraintes avant d'écrire la moindre ligne de code." },
  { n: "02", title: "Concevoir", text: "Définir une architecture solide et une stack technique adaptée à votre budget et vos ambitions." },
  { n: "03", title: "Développer", text: "Livrer un produit web ou mobile robuste, performant et pensé pour évoluer." },
  { n: "04", title: "Accompagner", text: "Suivi post-lancement, correctifs, évolutions : je reste à vos côtés après la mise en ligne." },
];

const services = [
  { icon: Smartphone, title: "Création d'applications", text: "Développement web et mobile (iOS/Android) pour tous domaines." },
  { icon: Wrench, title: "Suivi et Maintenance", text: "Accompagnement post-lancement, suivi technique et évolutions." },
  { icon: Compass, title: "Consulting & Freelance", text: "Conseil sur l'architecture, la stack technique et l'optimisation." },
];

const marqueeItems = ["Applications Web", "Mobile iOS / Android", "SaaS", "E-commerce", "B2B / B2C", "Consulting", "Freelance"];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const heroFade = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden">
        <motion.div
          aria-hidden="true"
          style={{ y: glowY }}
          className="absolute -top-32 -left-40 w-[560px] h-[560px] rounded-full bg-blue-600/10 blur-[120px]"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: "linear-gradient(#1e293b 1px, transparent 1px), linear-gradient(90deg, #1e293b 1px, transparent 1px)",
            backgroundSize: "72px 72px",
          }}
        />

        <motion.div style={{ opacity: heroFade }} className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full pt-28 pb-20">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-mono text-xs tracking-[0.25em] uppercase text-blue-500 mb-8"
            data-testid="hero-overline"
          >
            Développeur indépendant — Paris
          </motion.p>

          <h1 className="font-heading font-extrabold text-5xl sm:text-6xl lg:text-[5.5rem] tracking-tighter leading-[0.95]" data-testid="hero-title">
            <KineticText
              delay={0.25}
              lines={[
                <>Applications</>,
                <><span className="text-blue-500">web &amp; mobiles</span></>,
                <>sur mesure.</>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-lg sm:text-xl leading-relaxed text-slate-300"
            data-testid="hero-tagline"
          >
            MadCode — Développeur indépendant basé à Paris, spécialisé dans la
            création d'applications web et mobiles sur mesure.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <MagneticButton>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="group inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-4 transition-colors shadow-[0_0_40px_rgba(37,99,235,0.35)]"
              >
                Découvrir les services
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="group inline-flex items-center gap-2 text-slate-300 hover:text-white font-medium px-2 py-4 transition-colors border-b border-transparent hover:border-blue-500"
            >
              Me contacter
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-slate-800/60 py-6 bg-[#0d0d0d]" data-testid="home-marquee">
        <Marquee speed={35} gradient={false} autoFill>
          {marqueeItems.map((item, i) => (
            <span key={i} className="flex items-center">
              <span className="font-heading font-extrabold text-3xl sm:text-4xl text-stroke uppercase tracking-tight mx-8">
                {item}
              </span>
              <span className="text-blue-600 text-xl">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* MANIFESTO */}
      <section className="py-28 lg:py-40 px-6 lg:px-10" data-testid="home-manifesto">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-blue-500 mb-4">La méthode</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl">
              Un artisan du code, du premier échange à la mise en ligne.
            </h2>
          </Reveal>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-16">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div className="relative pl-6 border-l border-slate-800 group">
                  <span className="absolute -top-10 right-0 font-heading font-extrabold text-[6rem] leading-none text-slate-900 select-none pointer-events-none" aria-hidden="true">
                    {c.n}
                  </span>
                  <p className="font-mono text-sm text-blue-500 mb-3">{c.n}</p>
                  <h3 className="font-heading font-bold text-2xl mb-3 group-hover:text-blue-400 transition-colors">{c.title}</h3>
                  <p className="text-slate-400 leading-relaxed max-w-md">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-28 px-6 lg:px-10 border-t border-slate-800/60" data-testid="home-services-preview">
        <div className="max-w-7xl mx-auto">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
              <p className="font-mono text-xs tracking-[0.2em] uppercase text-blue-500 mb-4">Services</p>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">Ce que je fais</h2>
            </div>
            <Link
              to="/services"
              data-testid="home-services-link"
              className="group inline-flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              Tous les services
              <ArrowUpRight size={16} className="text-blue-500 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>

          <div className="divide-y divide-slate-800/60 border-y border-slate-800/60">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.07}>
                <Link
                  to="/services"
                  data-testid={`home-service-row-${i}`}
                  className="group flex items-center justify-between gap-6 py-8 px-2 hover:bg-blue-600/5 transition-colors"
                >
                  <div className="flex items-center gap-6">
                    <s.icon size={26} className="text-blue-500 shrink-0" />
                    <div>
                      <h3 className="font-heading font-bold text-xl sm:text-2xl group-hover:text-blue-400 transition-colors">{s.title}</h3>
                      <p className="text-sm text-slate-500 mt-1">{s.text}</p>
                    </div>
                  </div>
                  <ArrowUpRight size={22} className="text-slate-600 group-hover:text-blue-500 transition-all group-hover:translate-x-1 group-hover:-translate-y-1 shrink-0" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 px-6 lg:px-10 overflow-hidden" data-testid="home-cta">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/20895345/pexels-photo-20895345.jpeg)" }}
        />
        <div aria-hidden="true" className="absolute inset-0 bg-black/85" />
        <div className="relative max-w-7xl mx-auto">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-blue-500 mb-4">Un projet en tête ?</p>
            <h2 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-3xl leading-[1.02]">
              Transformons votre idée en produit.
            </h2>
            <div className="mt-10">
              <MagneticButton>
                <Link
                  to="/contact"
                  data-testid="home-cta-button"
                  className="group inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-4 transition-colors"
                >
                  Parlons-en
                  <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </MagneticButton>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
